import { useEffect, useState } from 'react';
import { Banner, Button, Dialog, Select, Skeleton, useToast } from '../../../../ds/components';
import { ApiError, errorMessage } from '../../../../lib/api';
import { useDeleteStage, useStageDeletion, type PipelineItem } from './api';
import styles from './PipelinesPage.module.css';

interface DeleteStageDialogProps {
  stage: PipelineItem | null;
  onClose: () => void;
}

function countSentence(count: number): string {
  return count === 1
    ? 'In diesem Stage steht 1 Deal. Wählen Sie, wohin er wechselt.'
    : `In diesem Stage stehen ${count} Deals. Wählen Sie, wohin sie wechseln.`;
}

/**
 * Admin 04 „Stage löschen": the active deals of the stage move to the chosen target (targets and default from
 * `GET /stages/:id/deletion`); the list shows every deal with its target, the history keeps „<Name> (gelöscht)".
 * A stage without deals gets a short confirmation.
 */
export function DeleteStageDialog({ stage, onClose }: DeleteStageDialogProps) {
  const { toast } = useToast();
  const deletion = useStageDeletion(stage?.id ?? null);
  const remove = useDeleteStage();
  const [targetId, setTargetId] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const data = deletion.data;

  useEffect(() => {
    if (!data) return;
    setTargetId(data.defaultTargetId ?? '');
    setFieldError(null);
    setBanner(null);
  }, [data]);

  if (!stage) return null;
  const current = stage;
  const hasDeals = (data?.deals.length ?? 0) > 0;
  const targetName = data?.targets.find((target) => target.id === targetId)?.name ?? '';

  async function submit() {
    setFieldError(null);
    setBanner(null);
    try {
      await remove.mutateAsync({ stageId: current.id, targetStageId: hasDeals ? targetId || null : null });
      toast({
        message: hasDeals
          ? `„${current.name}“ ist gelöscht. Die Deals stehen jetzt in „${targetName}“.`
          : `„${current.name}“ ist gelöscht.`,
      });
      onClose();
    } catch (error) {
      if (error instanceof ApiError && error.fields['targetStageId'])
        setFieldError(error.fields['targetStageId']);
      else setBanner(errorMessage(error));
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      eyebrow="Stage löschen"
      title={`„${current.name}“ löschen?`}
      subtitle={
        data
          ? hasDeals
            ? countSentence(data.deals.length)
            : 'In diesem Stage stehen keine Deals. Die Stage verschwindet aus der Pipeline.'
          : undefined
      }
      width={560}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            loading={remove.isPending}
            disabled={!data || (hasDeals && targetId === '')}
            onClick={() => void submit()}
          >
            {hasDeals ? 'Löschen und verschieben' : 'Löschen'}
          </Button>
        </>
      }
    >
      {deletion.isError ? <Banner tone="danger">{errorMessage(deletion.error)}</Banner> : null}
      {deletion.isPending ? (
        <div className={styles.dialogBody} aria-busy="true">
          <Skeleton variant="block" height={40} />
          <Skeleton width="70%" />
        </div>
      ) : null}
      {data && hasDeals ? (
        <div className={styles.dialogBody}>
          <Select
            label="Deals verschieben nach"
            value={targetId}
            placeholder="Stage wählen"
            options={data.targets.map((target) => ({ value: target.id, label: target.name }))}
            hint="Nur Stages ohne Pflichtangaben, die diese Deals nicht erfüllen, stehen zur Wahl."
            error={fieldError ?? undefined}
            onChange={(event) => setTargetId(event.target.value)}
          />
          <ul className={styles.mapping} aria-label="Betroffene Deals" style={{ margin: 0, padding: 0 }}>
            {data.deals.map((deal) => (
              <li key={deal.id} className={styles.mappingRow} style={{ listStyle: 'none' }}>
                <span className={styles.mappingName}>{deal.contactShortName}</span>
                <span className={styles.mappingTarget}>→ {targetName}</span>
              </li>
            ))}
          </ul>
          <div className={styles.info}>
            Die Stage-Historie der Deals bleibt. Dort steht „{current.name} (gelöscht)“.
          </div>
        </div>
      ) : null}
      {banner ? <Banner tone="danger">{banner}</Banner> : null}
    </Dialog>
  );
}
